import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Account, AccountADFormValues, ADRestoration } from '@/client/accounts';
import {
  LoadingStateType,
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { useNotifications } from '@/hooks/notifications.hook';
import {
  useAccount,
  useADConfig,
  useForceADSync,
  useSaveADConfig,
} from '@/hooks/query';
import { OPERATION_LOGS_QUERY_KEY } from '@/hooks/query/operation-log.hooks';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import {
  selectCurrentAccount,
  setCurrentAccount,
} from '@/store/features/account';
import { transformMappingValues } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';

export const useAzureADHandler = (id: string) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { account, isLoading: isAccountLoading } = useAccount({
    accountId: id,
  });
  const {
    accountAd,
    refetch,
    isLoading: isADLoading,
  } = useADConfig({ accountId: id });
  const currentAccount = useAppSelector(selectCurrentAccount);
  const dispatch = useAppDispatch();
  const forceADSync = useForceADSync();
  const saveADConfig = useSaveADConfig();

  const [syncState, setSyncState] = useState<LoadingStateType>(
    LoadingStatuses.IDLE,
  );
  const [restorations, setRestorations] = useState<ADRestoration[]>([]);
  const [showRestorationsDialog, setShowRestorationsDialog] = useState(false);
  const [initialValues, setInitialValues] = useState<AccountADFormValues>({
    active: false,
    checkActiveDirectoryEnabledStatus: false,
    clientId: '',
    clientSecret: '',
    hasClientSecret: undefined,
    tenantId: '',
    mapping: {
      [v4()]: {
        groupId: '',
        account: account,
        branches: [],
        groups: [],
        language: LanguagesEnum.EN,
      },
    },
    hasAd: false,
  });

  const {
    lastMessage,
    notificationParam: accountId,
    setNotificationParam: setAccountId,
  } = useNotifications(client.adSync.adSyncNotifyUrl);

  useEffect(() => {
    if (!accountId) return;
    handleSyncNotification(lastMessage);
  }, [JSON.stringify(lastMessage)]);

  useEffect(() => {
    if (!account || currentAccount?.id !== account.id) return;

    updateCurrentAccount(account);
  }, [account, currentAccount]);

  const updateCurrentAccount = (account: Account) => {
    if (JSON.stringify(account) !== JSON.stringify(currentAccount)) {
      dispatch(setCurrentAccount(account));
    }
  };

  useEffect(() => {
    if (!accountAd || !account) return;

    setInitialValues({
      active: accountAd.active || false,
      checkActiveDirectoryEnabledStatus:
        accountAd.config?.checkActiveDirectoryEnabledStatus || false,
      clientId: accountAd.config?.clientId || '',
      clientSecret: accountAd.config?.clientSecret || '',
      hasClientSecret: !!accountAd.config?.hasClientSecret,
      tenantId: accountAd.config?.tenantId || '',
      mapping: transformMappingValues(
        accountAd.mapping,
        initialValues.mapping,
        currentAccount,
        account,
      ),
      hasAd:
        !!accountAd.config?.clientId &&
        (!!accountAd.config?.clientSecret ||
          !!accountAd.config?.hasClientSecret) &&
        !!accountAd.config?.tenantId,
    });
  }, [accountAd, account]);

  const handleForceADSync = async (accountId: string) => {
    if (!accountId) return;

    try {
      setSyncState(LoadingStatuses.LOADING);

      const { restorations: detectedRestorations } =
        await client.adSync.previewConflicts(accountId);

      if (detectedRestorations.length > 0) {
        setRestorations(detectedRestorations);
        setShowRestorationsDialog(true);
        setSyncState(LoadingStatuses.IDLE);
        return;
      }

      setAccountId(accountId);
      await forceADSync.sync(accountId, false);

      toast?.info(t('toast.info'), t('job.created'));
    } catch (e) {
      setSyncState(LoadingStatuses.IDLE);
      setAccountId(undefined);
      toast?.action({
        severity: 'error',
        summary: t('toast.error'),
        detail: t('sync.failed.config'),
        actionLabel: t('generic.view.details'),
        onAction: () => {
          navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_ERRORS]());
        },
      });
    }
  };

  const handleRestorationsClose = () => {
    setShowRestorationsDialog(false);
    setRestorations([]);
    setSyncState(LoadingStatuses.IDLE);
  };

  const handleRestorationsConfirm = async () => {
    setShowRestorationsDialog(false);
    setRestorations([]);

    try {
      setSyncState(LoadingStatuses.LOADING);
      setAccountId(id);
      await forceADSync.sync(id, false);

      toast?.info(t('toast.info'), t('job.created'));
    } catch (e) {
      setSyncState(LoadingStatuses.IDLE);
      setAccountId(undefined);
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleOverwriteConfirm = async () => {
    setShowRestorationsDialog(false);
    setRestorations([]);

    try {
      setSyncState(LoadingStatuses.LOADING);
      setAccountId(id);
      await forceADSync.sync(id, true);

      toast?.info(t('toast.info'), t('job.created'));
    } catch (e) {
      setSyncState(LoadingStatuses.IDLE);
      setAccountId(undefined);
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleSyncNotification = (
    messages: { data: any; lastEventId: string } | null,
  ) => {
    if (
      !messages?.data?.event ||
      messages.data.event === 'active-directory.account.sync.started' ||
      !['active-directory.account.synced', 'ping'].includes(
        messages?.data?.event,
      ) ||
      (messages?.data?.event === 'ping' && Number(messages?.lastEventId) <= 10)
    ) {
      return;
    }

    if (messages.data.event === 'ping') {
      toast?.info(t('sync.status.pending'), t('sync.inProgress'));
      return;
    }

    if (messages?.data?.error && messages?.data?.error.length > 0) {
      const errorCount = Array.isArray(messages.data.error)
        ? messages.data.error.length
        : 1;

      toast?.action({
        severity: 'error',
        summary: t('toast.error'),
        detail: t('sync.issues.count', { count: errorCount }),
        actionLabel: t('generic.view.details'),
        onAction: () => {
          navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_ERRORS]());
        },
      });
    }

    if (
      messages.data.event === 'active-directory.account.synced' &&
      (!messages?.data?.error || messages?.data?.error.length === 0)
    ) {
      toast?.success(t('toast.success'), t('account.azure.synced'));
    }

    setSyncState(LoadingStatuses.IDLE);
    setAccountId(undefined);
    refetch();
    queryClient.invalidateQueries(OPERATION_LOGS_QUERY_KEY);
  };

  const handleUpdateAzureAD = async (data: AccountADFormValues) => {
    await saveADConfig.create({
      updates: {
        active: data.active,
        config: {
          clientId: data.clientId,
          clientSecret: data?.clientSecret ? data.clientSecret : undefined,
          tenantId: data.tenantId,
          checkActiveDirectoryEnabledStatus:
            data.checkActiveDirectoryEnabledStatus,
        },
        mapping: data.active
          ? Object.values(data.mapping).map((value) => ({
              groupId: value.groupId,
              branches: value.account
                ? []
                : value.branches.map((branch) => ({ id: branch.id })),
              groups: value.groups.map((group) => ({ id: group.id })),
              language: value.language,
            }))
          : undefined,
      },
      accountId: id as string,
    });
  };

  const handleSubmit = async (data: AccountADFormValues) => {
    try {
      if (!id || !data) return false;

      await handleUpdateAzureAD(data);

      toast?.success(t('toast.success'), t('account.azure.updated'));
      refetch();

      if (data.active) {
        await handleForceADSync(id);
      }

      return true;
    } catch (e) {
      setSyncState(LoadingStatuses.IDLE);
      setAccountId(undefined);
      handleAxiosError(e as Error | AxiosError, toast);
      return false;
    }
  };

  return {
    initialValues,
    syncState,
    saveADConfig,
    accountAd,
    handleSubmit,
    handleForceADSync,
    isAccountLoading,
    isADLoading,
    restorations,
    showRestorationsDialog,
    handleRestorationsConfirm,
    handleOverwriteConfirm,
    handleRestorationsClose,
  };
};
